import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiClock, FiHeart, FiStar, FiTarget } = FiIcons;

const About = () => {
  const stats = [
    { icon: FiUsers, number: '5000+', label: 'Happy Clients' },
    { icon: FiClock, number: '8+', label: 'Years Experience' },
    { icon: FiAward, number: '50+', label: 'Awards Won' },
    { icon: FiStar, number: '4.9', label: 'Average Rating' }
  ];

  const teamMembers = [
    {
      name: 'Mohamed',
      role: 'Master Barber',
      experience: '8 years',
      specialty: 'Classic & Modern Cuts',
      image: 'https://i.ibb.co/qLGgY7dH/Nashmi-barber-shop-Cutting-1.jpg',
      description: 'Authentic Egyptian barber with exceptional skills in traditional and contemporary styling.'
    },
    {
      name: 'Ahmad',
      role: 'Senior Barber',
      experience: '6 years',
      specialty: 'Beard Styling',
      image: 'https://i.ibb.co/B24tDf5q/Nashmi-barber-shop-Cutting-1.jpg',
      description: 'Expert in precision beard trimming and modern grooming techniques.'
    },
    {
      name: 'Nader',
      role: 'Professional Barber',
      experience: '5 years',
      specialty: 'Hair Treatments',
      image: 'https://i.ibb.co/C3x4vDY8/Nashmi-barber-shop-Cutting-2.jpg',
      description: 'Specialist in hair treatments and scalp care with a gentle touch.'
    }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Passion',
      description: 'We are passionate about our craft and dedicated to delivering exceptional results.'
    },
    {
      icon: FiAward,
      title: 'Excellence',
      description: 'We strive for excellence in every service we provide, ensuring the highest quality.'
    },
    {
      icon: FiUsers,
      title: 'Community',
      description: 'We believe in building strong relationships with our clients and community.'
    },
    {
      icon: FiTarget,
      title: 'Precision',
      description: 'Every cut, every style is executed with precision and attention to detail.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-luxury-charcoal to-luxury-darkCharcoal text-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-bold mb-6">
              About <span className="text-luxury-gold">Nashmi Barber Shop</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover the story behind Dubai's premier destination for men's grooming and style
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">
                Our <span className="text-luxury-gold">Story</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                Located in the prestigious Fortune Tower in Jumeirah Lake Towers, Nashmi Barber Shop has been 
                serving Dubai's discerning gentlemen for over 8 years. What started as a small vision has grown 
                into one of Dubai's most trusted destinations for premium men's grooming.
              </p>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                Our team of skilled professionals brings together traditional barbering techniques with modern 
                styling trends, ensuring every client leaves feeling confident and looking their absolute best.
              </p>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                We pride ourselves on creating a welcoming atmosphere where professionalism meets comfort, 
                making every visit a memorable experience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://i.ibb.co/4RrcPL5H/Nashmi-barber-shop-inside-image-1.jpg"
                alt="Shop Interior"
                className="rounded-lg shadow-lg"
              />
              <img
                src="https://i.ibb.co/HLj4vV96/Nashmi-barber-shop-inside-image-2.jpg"
                alt="Professional Service"
                className="rounded-lg shadow-lg mt-8"
              />
              <img
                src="https://i.ibb.co/v6hm87mj/Nashmi-barber-shop-inside-image-3.jpg"
                alt="Modern Equipment"
                className="rounded-lg shadow-lg -mt-8"
              />
              <img
                src="https://i.ibb.co/xq3s6C97/Nashmi-barber-shop-Inside.jpg"
                alt="Luxury Ambiance"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-luxury-gold">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center text-black"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <SafeIcon icon={stat.icon} className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-3xl font-bold mb-2">{stat.number}</h3>
                <p className="font-semibold">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">
              Meet Our <span className="text-luxury-gold">Team</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Our skilled professionals are the heart of Nashmi Barber Shop, bringing years of experience and passion to every service
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                className="bg-white dark:bg-luxury-charcoal rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                  <p className="text-luxury-gold font-semibold mb-1">{member.role}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{member.experience} • {member.specialty}</p>
                  <p className="text-gray-600 dark:text-gray-300">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50 dark:bg-luxury-charcoal">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-luxury-gold">Values</span>
            </h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              The principles that guide everything we do at Nashmi Barber Shop
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="bg-luxury-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">
                Visit <span className="text-luxury-gold">Our Shop</span>
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Location</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Fortune Tower - Cluster C<br />
                    Jumeirah Lake Towers<br />
                    Dubai - United Arab Emirates
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Operating Hours</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Daily: 9:00 AM - 11:00 PM<br />
                    Walk-ins welcome<br />
                    Appointments preferred
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Contact</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Phone: +971 50 709 6230<br />
                    Free parking available
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-200 dark:bg-luxury-darkCharcoal rounded-lg overflow-hidden shadow-lg h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1313.7297067268025!2d55.13733400757098!3d25.068410078444987!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6d7fd8a42d89%3A0x50f5bb489caafc3b!2sNashmi%20barber%20shop!5e1!3m2!1sen!2s!4v1757104631974!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nashmi Barber Shop Location"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;